/**
 * Loader Component
 * Loading spinners and indicators
 */

import { Loader2 } from 'lucide-react';

export interface LoaderProps {
    size?: 'sm' | 'md' | 'lg' | 'xl';
    variant?: 'spinner' | 'dots' | 'pulse';
    className?: string;
    message?: string;
    fullScreen?: boolean;
}

export function Loader({
    size = 'md',
    variant = 'spinner',
    className = '',
    message,
    fullScreen = false,
}: LoaderProps) {
    const sizeClasses = {
        sm: 'w-4 h-4',
        md: 'w-6 h-6',
        lg: 'w-8 h-8',
        xl: 'w-12 h-12',
    };

    const textSizes = {
        sm: 'text-xs',
        md: 'text-sm',
        lg: 'text-base',
        xl: 'text-lg',
    };

    const content = (
        <div className={`flex flex-col items-center justify-center gap-3 ${className}`}>
            {variant === 'spinner' && (
                <Loader2 className={`${sizeClasses[size]} animate-spin text-primary`} />
            )}
            {variant === 'dots' && (
                <div className="flex items-center gap-1">
                    {[0, 1, 2].map((i) => (
                        <div
                            key={i}
                            className={`${size === 'sm' ? 'w-1.5 h-1.5' : size === 'md' ? 'w-2 h-2' : size === 'lg' ? 'w-2.5 h-2.5' : 'w-3 h-3'}
                                rounded-full bg-primary animate-bounce`}
                            style={{ animationDelay: `${i * 0.15}s` }}
                        />
                    ))}
                </div>
            )}
            {variant === 'pulse' && (
                <div className={`${sizeClasses[size]} rounded-full bg-primary animate-pulse`} />
            )}
            {message && (
                <p className={`${textSizes[size]} text-gray-500`}>{message}</p>
            )}
        </div>
    );

    if (fullScreen) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm">
                {content}
            </div>
        );
    }

    return content;
}

export function PageLoader({ message = 'Loading...' }: { message?: string }) {
    return (
        <div className="flex items-center justify-center min-h-[400px]">
            <Loader size="lg" message={message} />
        </div>
    );
}

export function FullPageLoader({ message = 'Loading...' }: { message?: string }) {
    return <Loader size="xl" message={message} fullScreen />;
}

/**
 * LoadingContainer - Centered loading spinner with consistent padding
 * Replaces repeated: <div className="flex items-center justify-center py-12"><LoadingSpinner /></div>
 */
export interface LoadingContainerProps {
    size?: 'sm' | 'md' | 'lg' | 'xl';
    message?: string;
    className?: string;
}

export function LoadingContainer({
    size = 'lg',
    message,
    className = '',
}: LoadingContainerProps) {
    return (
        <div
            role="status"
            aria-busy="true"
            aria-label="Loading"
            data-testid="loading-container"
            className={`flex items-center justify-center py-12 ${className}`}
        >
            <Loader size={size} message={message} />
        </div>
    );
}

export default Loader;
